/**
 * Bookd - Mapbox Geocoding Utility
 * Free tier: 100,000 requests/month
 * Uses Mapbox Geocoding API for address-to-coordinates conversion
 */

// Mapbox public token - This is a public token for client-side usage
// The free tier allows 100,000 requests/month which is plenty for Bookd
const MAPBOX_ACCESS_TOKEN = 'pk.eyJ1IjoibWFwYm94IiwiYSI6ImNpejY4NXVycTA2emYycXBndHRqcmZ3N3gifQ.rJcFIG214AriISLbB6B5aw';

// Fallback coordinates for common cities (used if Mapbox fails)
const CITY_COORDS = {
  'new york': [40.7128, -74.0060],
  'nyc': [40.7128, -74.0060],
  'brooklyn': [40.6782, -73.9442],
  'manhattan': [40.7831, -73.9712],
  'los angeles': [34.0522, -118.2437],
  'la': [34.0522, -118.2437],
  'chicago': [41.8781, -87.6298],
  'miami': [25.7617, -80.1918],
  'atlanta': [33.7490, -84.3880],
  'houston': [29.7604, -95.3698],
  'dallas': [32.7767, -96.7970],
  'san francisco': [37.7749, -122.4194],
  'sf': [37.7749, -122.4194],
  'seattle': [47.6062, -122.3321],
  'boston': [42.3601, -71.0589],
  'denver': [39.7392, -104.9903],
  'nashville': [36.1627, -86.7816],
  'austin': [30.2672, -97.7431],
  'phoenix': [33.4484, -112.0740],
  'philadelphia': [39.9526, -75.1652],
  'san diego': [32.7157, -117.1611],
  'las vegas': [36.1699, -115.1398],
  'portland': [45.5155, -122.6789],
  'default': [39.8283, -98.5795] // Center of US
};

// Cache for geocoded addresses
const geocodeCache = new Map();

/**
 * Geocode an address to coordinates using Mapbox
 * @param {string} address - Full address or city name
 * @param {string} city - City name (optional, for better accuracy)
 * @param {string} state - State name (optional)
 * @returns {Promise<[number, number] | null>} - [lat, lng] or null if failed
 */
export const geocodeAddress = async (address, city = '', state = '') => {
  // Build search query
  const searchQuery = [address, city, state].filter(Boolean).join(', ');
  
  if (!searchQuery) {
    return CITY_COORDS.default;
  }

  // Check cache first
  const cacheKey = searchQuery.toLowerCase();
  if (geocodeCache.has(cacheKey)) {
    return geocodeCache.get(cacheKey);
  }

  // Try Mapbox geocoding
  try {
    const encodedQuery = encodeURIComponent(searchQuery);
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedQuery}.json?access_token=${MAPBOX_ACCESS_TOKEN}&limit=1&types=address,poi,place,locality`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Mapbox API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.features && data.features.length > 0) {
      const [lng, lat] = data.features[0].center;
      const coords = [lat, lng]; // Leaflet uses [lat, lng]
      
      // Cache the result
      geocodeCache.set(cacheKey, coords);
      
      return coords;
    }
  } catch (error) {
    console.error('Mapbox geocoding error:', error);
  }
  
  // Fallback to city-based lookup
  return getCityCoords(city || address);
};

/**
 * Get coordinates for a city using the fallback lookup
 * @param {string} city - City name
 * @returns {[number, number]} - [lat, lng]
 */
export const getCityCoords = (city) => {
  const cityLower = (city || '').toLowerCase();
  
  for (const [key, coords] of Object.entries(CITY_COORDS)) {
    if (cityLower.includes(key) || key.includes(cityLower)) {
      return coords;
    }
  }
  
  return CITY_COORDS.default;
};

/**
 * Geocode a venue object
 * @param {Object} venue - Venue object with address, city, state fields
 * @returns {Promise<[number, number]>} - [lat, lng]
 */
export const geocodeVenue = async (venue) => {
  if (!venue) return CITY_COORDS.default;
  
  // If venue already has coordinates, use them
  if (venue.latitude && venue.longitude) {
    return [venue.latitude, venue.longitude];
  }
  
  // Build full address
  const fullAddress = [
    venue.address,
    venue.city,
    venue.state,
    venue.zip_code
  ].filter(Boolean).join(', ');
  
  // Geocode the address
  const coords = await geocodeAddress(fullAddress, venue.city, venue.state);
  
  // Add small random offset to prevent marker overlap
  return [
    coords[0] + (Math.random() - 0.5) * 0.005,
    coords[1] + (Math.random() - 0.5) * 0.005
  ];
};

/**
 * Batch geocode multiple venues
 * @param {Array} venues - Array of venue objects
 * @returns {Promise<Map<string, [number, number]>>} - Map of venue_id to coordinates
 */
export const batchGeocodeVenues = async (venues) => {
  const results = new Map();
  
  // Process in batches of 5 to avoid rate limiting
  const batchSize = 5;
  for (let i = 0; i < venues.length; i += batchSize) {
    const batch = venues.slice(i, i + batchSize);
    
    const promises = batch.map(async (venue) => {
      const coords = await geocodeVenue(venue);
      return { venue_id: venue.venue_id, coords };
    });
    
    const batchResults = await Promise.all(promises);
    batchResults.forEach(({ venue_id, coords }) => {
      results.set(venue_id, coords);
    });
    
    // Small delay between batches
    if (i + batchSize < venues.length) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }
  
  return results;
};

/**
 * Reverse geocode coordinates to address
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<string | null>} - Address string or null
 */
export const reverseGeocode = async (lat, lng) => {
  try {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_ACCESS_TOKEN}&limit=1`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Mapbox API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.features && data.features.length > 0) {
      return data.features[0].place_name;
    }
  } catch (error) {
    console.error('Mapbox reverse geocoding error:', error);
  }
  
  return null;
};

export { CITY_COORDS };
